import { BLEEDING_JEWEL_ID, type JewelSocketProfile, toExactBigInt } from '@kenocyber/shared';
import { BPS_ONE } from './exact';
import type { DamageOrigin } from './origin';
import { damageOriginMayTrigger } from './origin';
import {
  PROC_CHANCE_DENOMINATOR,
  PROC_LEVEL_CAP,
  procChanceNumerator,
  procDamageMilli,
} from './proc-lottery';

/**
 * **A Garra Hemorrágica: uma MARCA por acerto direto, e nada acontece até o
 * jogador mandar.**
 *
 * ## O que ela é, e o que a separa das cinco joias que já existem
 *
 * O Ciclone, a Bomba e o Míssil produzem um GOLPE, e por isso disputam o mesmo
 * espaço de rolagem: duas ao mesmo tempo seriam um pico de dano num só quadro.
 * O Orbe da Víbora aplica ESTADO, e por isso tem sorteio próprio: um stack não
 * tem pico a evitar, porque no instante em que nasce não acontece nada.
 *
 * O Sangramento é essa ideia levada ao fim: nada acontece **nunca**, até que o
 * jogador carregue no botão. Uma marca não expira, não se renova, não cobra
 * parcela nenhuma e não tem relógio. Ela é um número congelado à espera de uma
 * ORDEM, e a ordem é uma ação de combate explícita.
 *
 * Fica fora da loteria ofensiva pela mesma razão que o veneno fica, e tem
 * **fluxo próprio** pela mesma razão: uma marca e um corte podem nascer do mesmo
 * acerto, e duas coisas que podem acontecer ao mesmo tempo precisam de dois
 * sorteios.
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não sorteia - a rolagem vem feita, do fluxo autoritativo do participante, e
 * quem a faz é a simulação. Não conhece um alvo, um relógio, uma arena, um mob
 * nem um casco: recebe números e devolve números. Não decide quando se detona, e
 * não sabe que existe um corte carmesim.
 */

/* -------------------------------------------------------------------------- */
/*  A chance                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * O teto de chance da Garra Hemorrágica: **13 %**, nomeado uma vez.
 *
 * É o número que produz, através de {@link bleedingChanceNumerator} e do mesmo
 * denominador derivado que as outras loterias usam, exatamente a tabela que o
 * gate V28G.N declara:
 *
 * | nível | numerador | chance |
 * | --- | --- | --- |
 * | 1 | 1 300 | 0,433333…% |
 * | 6 | 7 800 | 2,6 % |
 * | 15 | 19 500 | 6,5 % |
 * | 30 | 39 000 | **13 %**, o teto final |
 *
 * As quatro linhas saem desta única constante, sem tabela de exceções e sem
 * nenhum caso especial: `1300 x nível / 300000`. O nível 1 é a razão pela qual a
 * chance NUNCA é dividida antes de ser comparada - `13/300` é uma dízima
 * infinita (`300 = 2² x 3 x 5²`, e o três nunca desaparece), e comparar
 * `0,004333…` contra um número em vírgula flutuante seria decidir uma marca pelo
 * último bit de um `double`.
 *
 * Não há linha abaixo do 30. Um nível 31 é recusado por todas as fronteiras que
 * escrevem um perfil - base, socket, API, bilhete, instantânea - e a aritmética
 * defensiva devolve-lhe o mesmo que ao 30, porque é {@link procChanceNumerator}
 * que o garante e não uma verificação escrita aqui.
 *
 * É o mesmo teto do Ciclone de Lâminas e do Orbe da Víbora, e a coincidência é
 * de orçamento e não de cópia: as três gastam os 30 bps por nível do V28D.2
 * inteiros em dano, e o que as separa é QUANDO o dano acontece. Aqui, quando o
 * jogador quiser.
 */
export const BLEEDING_MAX_CHANCE_BPS = 1_300;

/**
 * O numerador desta joia, dado o seu nível somado.
 *
 * Inteiro, nunca dividido, e comparado contra {@link PROC_CHANCE_DENOMINATOR} -
 * o MESMO espaço de rolagem das outras loterias, porque a lei de chance é a
 * mesma lei. Acima de {@link PROC_LEVEL_CAP} devolve sempre o mesmo valor.
 */
export function bleedingChanceNumerator(levelSum: number): number {
  return procChanceNumerator(BLEEDING_MAX_CHANCE_BPS, levelSum);
}

/* -------------------------------------------------------------------------- */
/*  A magnitude                                                               */
/* -------------------------------------------------------------------------- */

/**
 * **O dano de UMA marca: 9/13 do dano-base autoritativo, em qualquer nível.**
 *
 * ## Porque 9/13, e porque não é um número escolhido aqui
 *
 * Porque é o que o orçamento do V28D.2 produz com este teto de chance e **toda**
 * a parcela paga em dano:
 *
 *     dano = base x 30 x min(nível,30) x 30 x 10000
 *            -------------------------------------  = base x 9/13
 *              1300 x min(nível,30) x 10000
 *
 * Os dois lados da fração crescem juntos com o nível e cancelam-se, portanto a
 * magnitude é **exatamente 9/13 ao nível 1 e ao nível 30** - o §"o nível altera
 * somente a chance, nunca a magnitude da marca", obtido por construção em vez de
 * por uma condição escrita à mão. Aproximadamente **69,230769 %**.
 *
 * Ao teto isso fecha a conta que o contrato declara:
 *
 *     13 % de chance x 9/13 do dano-base = 9 % de poder médio
 *
 * que são os 900 bps de poder de combate que o nível 30 vale, os mesmos da Chama
 * da Destruição, do Pulso do Dragão e do Orbe da Víbora.
 *
 * ## Uma divisão, e é a da loteria
 *
 * `procDamageMilli` é a função, não uma cópia dela: uma segunda implementação de
 * `9/13` seria uma segunda casa para o orçamento, e `bleeding.test.ts` prova a
 * igualdade contra `floor(base x 9 / 13)` nível a nível em vez de a assumir.
 *
 * `baseMilli` é o dano-base AUTORITATIVO da nave e mais nada: sem a Chama, sem
 * os Corações, sem runas, sem crítico e sem outro proc.
 */
export function bleedingMarkDamageMilli(baseMilli: bigint, levelSum: bigint): bigint {
  return procDamageMilli(baseMilli, BLEEDING_MAX_CHANCE_BPS, levelSum, BPS_ONE);
}

/**
 * **O dano bruto de uma detonação: a SOMA dos milli congelados.**
 *
 * ## Porque uma soma, e nunca `quantidade x dano atual`
 *
 * Porque cada marca congelou o SEU número no instante em que nasceu, e uma nave
 * que melhorou entretanto tem marcas de magnitudes diferentes acumuladas no
 * mesmo alvo. Multiplicar a contagem pelo dano de agora reavaliaria as antigas
 * pelo perfil de hoje - que é exatamente a leitura tardia que congelar existe
 * para eliminar, e a mesma fronteira temporal que `cutMilli` e
 * `totalDamageMilli` já impõem.
 *
 * ## E porque isso é LINEAR e nunca exponencial
 *
 * Porque a soma de `n` parcelas é `n` parcelas, e mais nada. Não há
 * multiplicador por marca, não há expoente e não há bónus por quantidade: dez
 * marcas valem dez marcas. O contrato diz-o em tabela, e esta aritmética não tem
 * por onde produzir outra coisa.
 *
 * Em `bigint` do princípio ao fim, e sem divisão nenhuma: não há resto a perder.
 */
export function bleedingDetonationMilli(
  marks: Iterable<Pick<BleedingMarkCore, 'damageMilli'>>,
): bigint {
  let total = 0n;
  for (const mark of marks) {
    total += mark.damageMilli;
  }
  return total;
}

/* -------------------------------------------------------------------------- */
/*  A seleção                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * O que uma aplicação de sangramento congela no instante em que nasce.
 *
 * Um campo só, e é o RESULTADO e nunca a receita - a mesma lei que congela o
 * `cutMilli` do Ciclone, o `blastMilli` da Bomba, o `damageMilli` do míssil e o
 * `totalDamageMilli` do veneno. Guardar `levelSum` obrigaria a reavaliar a
 * fórmula na detonação, e uma reavaliação responde "quanto vale agora" em vez de
 * "quanto valia quando esta marca nasceu". Uma marca não expira: o intervalo
 * entre a aplicação e a detonação é tempo de sobra para comprar um ponto, trocar
 * de nave ou desencaixar a própria joia.
 */
export interface BleedingApplication {
  readonly damageMilli: bigint;
}

/**
 * **A seleção.** Uma rolagem por acerto direto, e no máximo uma marca.
 *
 * `roll` tem de vir de `[0, PROC_CHANCE_DENOMINATOR)` e ser produzido pelo fluxo
 * autoritativo do participante - um fluxo SÓ para isto, porque partilhá-lo com a
 * loteria ofensiva ou com o veneno deslocaria a sequência de ambos.
 *
 * O intervalo é `[0, w)` e não tem vizinhos: este sorteio tem um só ocupante,
 * portanto não há ordem canónica a fixar nem cursor a avançar - exatamente como
 * o do veneno, e pela mesma razão.
 *
 * Devolve `null` quando a rolagem cai fora, quando a nave não tem a joia, e
 * quando a origem não pode originar joia nenhuma. As três razões são diferentes
 * e nenhuma produz marca.
 */
export function selectBleedingApplication(
  profile: JewelSocketProfile,
  origin: DamageOrigin,
  roll: number,
  baseMilli: bigint,
): BleedingApplication | null {
  /*
   * **A tabela fechada, nas suas DUAS metades** (gate V28G.P).
   *
   * A primeira diz se esta origem pode originar alguma coisa; a segunda diz se
   * ela pode originar ESTA. Uma serra pode, um tiro de canhão pode, um pet não
   * pode e um efeito de joia não pode - e nenhuma das duas simulações escreve a
   * diferença como uma condição sua.
   */
  if (!damageOriginMayTrigger(origin, 'bleed_detonation')) {
    return null;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= PROC_CHANCE_DENOMINATOR) {
    throw new RangeError(`bleeding roll outside [0, ${PROC_CHANCE_DENOMINATOR}): ${roll}`);
  }
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a bleeding base is a non-negative bigint, received ${String(baseMilli)}`);
  }
  const owned = profile.entries.find((entry) => entry.id === BLEEDING_JEWEL_ID);
  if (!owned) {
    return null;
  }
  const levelSum = toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
  const width = bleedingChanceNumerator(Number(levelSum));
  if (width <= 0 || roll >= width) {
    return null;
  }
  return Object.freeze({ damageMilli: bleedingMarkDamageMilli(baseMilli, levelSum) });
}

/* -------------------------------------------------------------------------- */
/*  A marca                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * **O que uma marca guarda, e o que ela deliberadamente NÃO guarda.**
 *
 * Os dois modos estendem isto com o vocabulário deles - o duelo endereça por
 * participante, a caça por id de entidade - e partilham exatamente estes três
 * campos porque são os únicos que a aritmética acima lê.
 *
 * ## Não há `ticksDone`, e não há `expiresAt`
 *
 * Porque uma marca não tem relógio. Ela não expira, não se renova e não cobra
 * nada com o tempo: o que decide quando ela paga é uma ORDEM do jogador, e uma
 * ordem não é um instante que se possa guardar antes de ela chegar. Um campo de
 * duração aqui seria uma promessa que nada cumpriria.
 *
 * ## `appliedAt` está no relógio do MODO, e serve a ORDEM
 *
 * O duelo conta tiques de 50 ms e a caça conta milissegundos de 16 em 16. O
 * campo não temporiza coisa nenhuma - guarda QUANDO, para que a reconstrução de
 * uma instantânea produza a mesma sequência que a simulação viva tinha, e para
 * que um registo diga o que aconteceu. O desempate de ordem é o
 * {@link BleedingMarkCore.id}, que é monotónico por construção.
 */
export interface BleedingMarkCore {
  /** Identidade estável, única por simulação e monotónica. Sobrevive ao disco. */
  readonly id: number;
  /** O dano congelado na aplicação, em milli. Nunca relido de um perfil. */
  readonly damageMilli: bigint;
  /** O instante da aplicação, no relógio do modo. Ordem, nunca temporizador. */
  readonly appliedAt: number;
}

/**
 * O teto de marcas que uma simulação guarda, e **não existe**.
 *
 * Declarado como ausência para que a sua falta seja uma decisão lida em vez de
 * um esquecimento: o contrato diz "não existe teto mecânico de stacks", e um
 * `.max(N)` - na lista, no fio ou no documento - seria uma decisão de equilíbrio
 * tomada por um validador.
 *
 * O que limita quantas existem é a aritmética que já existe: um acerto direto
 * aplica no máximo uma, a chance é finita, e o jogador que as acumula é o mesmo
 * que as gasta.
 */
export const BLEEDING_MARK_CEILING: null = null;

/* -------------------------------------------------------------------------- */
/*  Os fluxos                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Os rótulos dos fluxos de SANGRAMENTO, versionados como todos os outros.
 *
 * ## Porque um fluxo próprio, por participante e por finalidade
 *
 * Porque o contrato o exige, e a razão é a de sempre: consumir a rolagem do
 * sangramento de um fluxo partilhado deslocaria tudo o que viesse depois -
 * runas, spawns, temporizador do boss, pets, a rolagem por salva, a loteria
 * ofensiva, o veneno - e uma corrida com a mesma semente deixaria de ser a mesma
 * corrida por causa de uma joia.
 *
 * ## E porque ele é consumido CONDICIONALMENTE
 *
 * Porque serve uma única finalidade e mais ninguém o lê, que é a mesma razão
 * pela qual o fluxo do veneno e o de alvos do míssil também o são. A regra de
 * consumir sempre existe para fluxos cuja sequência OUTRA coisa observa; aqui a
 * única coisa que observa a sequência é o próximo acerto direto do mesmo
 * atacante, e a instantânea guarda o estado do gerador.
 */
export const PVP_BLEEDING_RNG_LABEL = 'pvp-jewel-bleeding-v1';
export const HUNT_BLEEDING_RNG_LABEL = 'hunt-jewel-bleeding-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_BLEEDING_RNG_LABEL = 'open-world-jewel-bleeding-v1';
